<template>
  <div ref="wrapperRef" :class="ns.b()">
    <el-input
      v-bind="mergeProps(passInputProps, $attrs)"
      ref="elInputRef"
      :model-value="modelValue"
      :disabled="disabled"
      :role="dropdownVisible ? 'combobox' : undefined"
      :aria-activedescendant="dropdownVisible ? hoveringId || '' : undefined"
      :aria-controls="dropdownVisible ? contentId : undefined"
      :aria-expanded="dropdownVisible || undefined"
      :aria-label="ariaLabel"
      :aria-autocomplete="dropdownVisible ? 'none' : undefined"
      :aria-haspopup="dropdownVisible ? 'listbox' : undefined"
      @input="handleInputChange"
      @keydown="handleInputKeyDown"
      @mousedown="handleInputMouseDown"
      @paste="handleInputPaste"
      @select="handleInputSelect"
    >
      <template v-for="(_, name) in $slots" #[name]="slotProps">
        <slot :name="name" v-bind="slotProps" />
      </template>
    </el-input>
    <el-tooltip
      ref="tooltipRef"
      :visible="dropdownVisible"
      :popper-class="[ns.e('popper'), popperClass]"
      :popper-options="popperOptions"
      :placement="computedPlacement"
      :fallback-placements="computedFallbackPlacements"
      effect="light"
      pure
      :offset="offset"
      :show-arrow="showArrow"
    >
      <template #default>
        <div :style="cursorStyle" />
      </template>
      <template #content>
        <el-mention-dropdown
          v-if="filteredOptions.length > 0 || loading"
          ref="dropdownRef"
          :options="filteredOptions"
          :disabled="disabled"
          :loading="loading"
          :content-id="contentId"
          :aria-label="ariaLabel"
          @select="handleSelect"
          @click.stop="elInputRef?.focus"
        >
          <template v-for="(_, name) in $slots" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps" />
          </template>
        </el-mention-dropdown>
        <slot v-else name="empty-placeholder" />
      </template>
    </el-tooltip>
  </div>
</template>

<script lang="ts" setup>
  import { computed, mergeProps, nextTick, ref } from 'vue'
  import { pick } from 'lodash-unified'
  import { useFocusController, useId, useNamespace } from 'element-plus/es/hooks/index.mjs'
  import ElInput, { inputProps } from 'element-plus/es/components/input/index.mjs'
  import ElTooltip from 'element-plus/es/components/tooltip/index.mjs'
  import { EVENT_CODE, INPUT_EVENT, UPDATE_MODEL_EVENT } from 'element-plus/es/constants/index.mjs'
  import { useFormDisabled } from 'element-plus/es/components/form/index.mjs'
  import { isFunction } from 'element-plus/es/utils/index.mjs'
  import { mentionEmits, mentionProps } from './mention'
  import { getCursorPosition, getMentionCtx } from 'element-plus/es/components/mention/src/helper'
  // @ts-ignore
  import ElMentionDropdown from 'element-plus/es/components/mention/src/mention-dropdown2.mjs'

  import type { CSSProperties } from 'vue'
  import type { InputInstance } from 'element-plus/es/components/input/'
  import type { TooltipInstance } from 'element-plus/es/components/tooltip/'
  import type { MentionCtx, MentionOption } from 'element-plus/es/components/mention/src/types'

  type Placement =
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'bottom-start'
    | 'left-start'
    | 'right-start'

  defineOptions({
    name: 'ElMention',
    inheritAttrs: false
  })

  const INPUT_SELECT = 'input-select'

  const props = defineProps(mentionProps)
  const emit = defineEmits(mentionEmits)

  const passInputProps = computed(() => pick(props, Object.keys(inputProps)))

  const ns = useNamespace('mention')
  const disabled = useFormDisabled()
  const contentId = useId()

  const elInputRef = ref<InputInstance>()
  const tooltipRef = ref<TooltipInstance>()
  const dropdownRef = ref<InstanceType<typeof ElMentionDropdown>>()

  const visible = ref(false)
  const cursorStyle = ref<CSSProperties>()
  const mentionCtx = ref<MentionCtx>()

  const computedPlacement = computed<Placement>(() =>
    props.showArrow ? props.placement : `${props.placement}-start`
  )

  const computedFallbackPlacements = computed<Placement[]>(() =>
    props.showArrow ? ['bottom', 'top'] : ['bottom-start', 'top-start']
  )

  const filteredOptions = computed(() => {
    const { filterOption, options } = props
    if (!mentionCtx.value || !filterOption) return options
    return options.filter(option => filterOption(mentionCtx.value!.pattern, option))
  })

  const dropdownVisible = computed(() => {
    return visible.value
  })

  const hoveringId = computed(() => {
    return `${contentId.value}-${dropdownRef.value?.hoveringIndex}`
  })

  const handleInputChange = (value: string) => {
    emit(UPDATE_MODEL_EVENT, value)
    emit(INPUT_EVENT, value, 'default')
    syncAfterCursorMove()
  }

  const handleInputSelect = (event: any) => {
    emit(INPUT_SELECT, event.target.selectionStart, event.target.selectionEnd)
  }

  const handleInputPaste = (event: ClipboardEvent) => {
    event.preventDefault()
    const inputEl = getInputEl()!
    const selectionEnd = inputEl.selectionEnd
    if (selectionEnd === null) {
      return
    }
    const selectionStart = inputEl.selectionStart ?? selectionEnd
    const inputValue = inputEl.value
    const pasteValue = event.clipboardData?.getData('text') || ''
    const value = inputValue.slice(0, selectionStart) + pasteValue + inputValue.slice(selectionEnd)
    emit(UPDATE_MODEL_EVENT, value)
    emit(INPUT_EVENT, value, 'paste')
    const newSelectionEnd = selectionStart + pasteValue.length
    nextTick(() => {
      inputEl.selectionStart = newSelectionEnd
      inputEl.selectionEnd = newSelectionEnd
      inputEl.focus()
      syncDropdownVisible()
    })
  }

  const handleInputKeyDown = (event: KeyboardEvent | Event) => {
    if (!('code' in event) || elInputRef.value?.isComposing) return

    switch (event.code) {
      case EVENT_CODE.left:
      case EVENT_CODE.right:
        syncAfterCursorMove()
        break
      case EVENT_CODE.up:
      case EVENT_CODE.down:
        if (!visible.value) return
        event.preventDefault()
        dropdownRef.value?.navigateOptions(event.code === EVENT_CODE.up ? 'prev' : 'next')
        break
      case EVENT_CODE.enter:
      case EVENT_CODE.numpadEnter:
        if (!visible.value) return
        event.preventDefault()
        if (dropdownRef.value?.hoverOption) {
          dropdownRef.value?.selectHoverOption()
        } else {
          visible.value = false
        }
        break
      case EVENT_CODE.esc:
        if (!visible.value) return
        event.preventDefault()
        visible.value = false
        break
      case EVENT_CODE.backspace:
        if (props.whole && mentionCtx.value) {
          const { splitIndex, selectionEnd, pattern, prefixIndex, prefix } = mentionCtx.value
          const inputEl = getInputEl()
          if (!inputEl) return
          const inputValue = inputEl.value
          const matchOption = props.options.find(item => item.value === pattern)
          const isWhole = isFunction(props.checkIsWhole)
            ? props.checkIsWhole(pattern, prefix, selectionEnd)
            : matchOption
          if (isWhole && splitIndex !== -1 && splitIndex + 1 === selectionEnd) {
            event.preventDefault()
            const newValue = inputValue.slice(0, prefixIndex) + inputValue.slice(splitIndex + 1)
            // whole-remove 会把 [prefixIndex, selectionEnd) 整段删掉，
            // 先把真实删除区间通过 input-select 抛出，避免消费方依赖 v-model 更新后被甩到末尾的 DOM 光标
            emit(INPUT_SELECT, prefixIndex, selectionEnd)
            emit(UPDATE_MODEL_EVENT, newValue)
            emit(INPUT_EVENT, newValue, 'whole-remove')
            emit('whole-remove', pattern, prefix)

            const newSelectionEnd = prefixIndex
            nextTick(() => {
              // input value is updated
              inputEl.selectionStart = newSelectionEnd
              inputEl.selectionEnd = newSelectionEnd
              syncDropdownVisible()
            })
          }
        }
    }
  }

  const { wrapperRef } = useFocusController(elInputRef, {
    disabled,
    afterFocus() {
      syncAfterCursorMove()
    },
    beforeBlur(event) {
      return tooltipRef.value?.isFocusInsideContent(event)
    },
    afterBlur() {
      visible.value = false
    }
  })

  const handleInputMouseDown = () => {
    syncAfterCursorMove()
  }

  const handleSelect = (item: MentionOption) => {
    if (!mentionCtx.value) return
    const inputEl = getInputEl()
    if (!inputEl) return
    const inputValue = inputEl.value
    const { split } = props

    const newEndPart = inputValue.slice(mentionCtx.value.end)
    const alreadySeparated = newEndPart.startsWith(split)
    const newMiddlePart = `${item.value}${alreadySeparated ? '' : split}`
    const newValue = inputValue.slice(0, mentionCtx.value.start) + newMiddlePart + newEndPart
    const offset =
      mentionCtx.value.start +
      (item?.value?.length ?? 0) +
      (alreadySeparated ? 0 : 1) -
      mentionCtx.value.end

    emit(UPDATE_MODEL_EVENT, newValue)
    emit(INPUT_EVENT, newValue, 'select')
    emit('select', item, mentionCtx.value, offset)

    const newSelectionEnd =
      mentionCtx.value.start + newMiddlePart.length + (alreadySeparated ? 1 : 0)

    nextTick(() => {
      // input value is updated
      inputEl.selectionStart = newSelectionEnd
      inputEl.selectionEnd = newSelectionEnd
      emit(INPUT_SELECT, inputEl.selectionStart, inputEl.selectionEnd)
      inputEl.focus()
      syncDropdownVisible()
    })
  }

  const getInputEl = () =>
    props.type === 'textarea' ? elInputRef.value?.textarea : elInputRef.value?.input

  const syncAfterCursorMove = () => {
    // can't use nextTick(), get cursor position will be wrong
    setTimeout(() => {
      syncCursor()
      syncDropdownVisible()
      nextTick(() => tooltipRef.value?.updatePopper())
    }, 0)
  }

  const syncCursor = () => {
    const inputEl = getInputEl()
    if (!inputEl) return

    const caretPosition = getCursorPosition(inputEl)
    emit(INPUT_SELECT, inputEl.selectionStart, inputEl.selectionEnd)
    const inputRect = inputEl.getBoundingClientRect()
    const elInputRect = elInputRef.value!.$el.getBoundingClientRect()

    cursorStyle.value = {
      position: 'absolute',
      width: 0,
      height: `${caretPosition.height}px`,
      left: `${caretPosition.left + inputRect.left - elInputRect.left}px`,
      top: `${caretPosition.top + inputRect.top - elInputRect.top}px`
    }
  }

  const syncDropdownVisible = () => {
    const inputEl = getInputEl()
    if (document.activeElement !== inputEl) {
      visible.value = false
      return
    }
    const { prefix, split } = props
    mentionCtx.value = getMentionCtx(inputEl, prefix, split)
    if (mentionCtx.value && mentionCtx.value.splitIndex === -1) {
      visible.value = true
      emit('search', mentionCtx.value.pattern, mentionCtx.value.prefix)
      return
    }
    visible.value = false
  }

  defineExpose({
    input: elInputRef,
    tooltip: tooltipRef,
    dropdownVisible
  })
</script>
